import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/ui";
import { usePatrolsStore } from "../../patrolsStore";
import SiteMap from "../../components/SiteMap";
import { AvatarStack, Kpi, LoadBlock, PersonAvatar, Photo, ProgressBar, Toolbar, pctTone, relativeTime, timeOnly } from "../../components/console";
import { RoundBadge, SeverityBadge, useRondinesStatus } from "./shared";

// Tablero de Rondines: cumplimiento del día y de la semana, rondas por día,
// ranking por ruta, puntos más omitidos, incidencias recientes y el mapa de
// la ruta elegida con sus puntos de control en orden.

export default function RondinesDashboard() {
  const { t, i18n } = useTranslation("bases");
  const { dashboard: d, routes, checkpoints, rounds, hydrate } = usePatrolsStore();
  const { status, error, retry } = useRondinesStatus();
  const [routeId, setRouteId] = useState<string>("");
  const activeRoute = routes.find((r) => r.id === routeId) ?? routes[0];

  const points = useMemo(
    () =>
      checkpoints
        .filter((c) => c.route_id === activeRoute?.id && typeof c.latitude === "number" && typeof c.longitude === "number")
        .sort((a, b) => a.order - b.order)
        .map((c) => ({ id: c.id, lat: c.latitude as number, lng: c.longitude as number, label: `${c.order}. ${c.name}`, order: c.order, color: activeRoute?.color })),
    [checkpoints, activeRoute],
  );

  const now = Date.now();
  const live = useMemo(
    () =>
      rounds
        .filter((r) => r.status === "in_progress" || (r.status === "scheduled" && Date.parse(r.plannedStart) - now < 8 * 3600 * 1000))
        .sort((a, b) => a.plannedStart.localeCompare(b.plannedStart))
        .slice(0, 6),
    [rounds, now],
  );

  const maxDay = Math.max(1, ...(d?.days ?? []).map((x) => x.total));

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <Toolbar>
        <div className="flex items-center gap-2">
          <i className="ri-shield-check-line text-lg text-primary-500" />
          <h1 className="text-sm font-semibold text-content">{t("rondines.dashboard.title")}</h1>
        </div>
        <div className="ml-auto">
          <Button size="sm" variant="ghost" icon={<i className="ri-refresh-line" />} onClick={() => void hydrate()}>
            {t("common.refresh")}
          </Button>
        </div>
      </Toolbar>
      <div className="flex-1 overflow-y-auto p-4">
        <LoadBlock status={status} error={error} onRetry={retry} empty={!d || d.routes === 0} emptyIcon="ri-route-line" emptyTitle={t("rondines.dashboard.emptyTitle")} emptyText={t("rondines.dashboard.emptyText")}>
          {d ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
                <Kpi icon="ri-checkbox-circle-line" label={t("rondines.kpi.today")} value={d.today.compliancePct === null ? "—" : `${d.today.compliancePct}%`} progress={d.today.compliancePct} tone={pctTone(d.today.compliancePct)} hint={t("rondines.kpi.todayHint", { done: d.today.completed, total: d.today.total })} />
                <Kpi icon="ri-calendar-2-line" label={t("rondines.kpi.week")} value={d.week.compliancePct === null ? "—" : `${d.week.compliancePct}%`} progress={d.week.compliancePct} tone={pctTone(d.week.compliancePct)} hint={t("rondines.kpi.weekHint", { n: d.week.total })} />
                <Kpi icon="ri-pie-chart-line" label={t("rondines.kpi.partial")} value={d.week.partial} tone="warning" hint={t("rondines.kpi.missed", { n: d.week.missed })} />
                <Kpi icon="ri-play-circle-line" label={t("rondines.kpi.live")} value={d.today.inProgress} tone="info" hint={t("rondines.kpi.scheduled", { n: d.today.scheduled })} />
                <Kpi icon="ri-alarm-warning-line" label={t("rondines.kpi.incidents")} value={d.incidents.open} tone={d.incidents.bySeverity.critical ? "error" : "warning"} hint={t("rondines.kpi.critical", { n: d.incidents.bySeverity.critical ?? 0 })} />
                <Kpi icon="ri-route-line" label={t("rondines.kpi.routes")} value={d.activeRoutes} hint={t("rondines.kpi.checkpoints", { n: d.checkpoints })} />
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                <section className="rounded-xl border border-line bg-card p-4 xl:col-span-2">
                  <h2 className="mb-3 text-sm font-semibold text-content">{t("rondines.dashboard.byDay")}</h2>
                  <div className="flex h-44 items-end gap-3">
                    {d.days.map((day) => (
                      <div key={day.date} className="flex flex-1 flex-col items-center gap-1">
                        <span className="text-[11px] font-semibold tabular-nums text-content-secondary">{day.compliancePct === null ? "—" : `${day.compliancePct}%`}</span>
                        <div className="flex w-full max-w-10 flex-col-reverse overflow-hidden rounded-md bg-subtle" style={{ height: `${Math.max(6, (day.total / maxDay) * 128)}px` }} title={`${day.completed} / ${day.partial} / ${day.missed}`}>
                          <div className="bg-success" style={{ height: `${day.total ? (day.completed / day.total) * 100 : 0}%` }} />
                          <div className="bg-warning" style={{ height: `${day.total ? (day.partial / day.total) * 100 : 0}%` }} />
                          <div className="bg-error" style={{ height: `${day.total ? (day.missed / day.total) * 100 : 0}%` }} />
                        </div>
                        <span className="text-[11px] text-content-muted">{new Date(`${day.date}T12:00:00`).toLocaleDateString(i18n.language.startsWith("en") ? "en-US" : "es-MX", { weekday: "short", day: "numeric" })}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex flex-wrap gap-4 text-[11px] text-content-secondary">
                    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-success" /> {t("rondines.roundStatus.completed")}</span>
                    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-warning" /> {t("rondines.roundStatus.partial")}</span>
                    <span className="flex items-center gap-1"><span className="h-2 w-2 rounded-full bg-error" /> {t("rondines.roundStatus.missed")}</span>
                  </div>
                </section>

                <section className="rounded-xl border border-line bg-card p-4">
                  <h2 className="mb-3 text-sm font-semibold text-content">{t("rondines.dashboard.live")}</h2>
                  {live.length === 0 ? <p className="text-xs text-content-muted">{t("rondines.dashboard.noLive")}</p> : null}
                  <ul className="space-y-2.5">
                    {live.map((r) => (
                      <li key={r.id} className="flex items-center gap-2">
                        <PersonAvatar name={r.guard?.name ?? "?"} size="sm" />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm text-content">{r.routeName}</div>
                          <div className="text-[11px] text-content-muted">
                            {timeOnly(r.plannedStart, i18n.language)} · {r.guard?.name}
                          </div>
                        </div>
                        <RoundBadge status={r.status} />
                      </li>
                    ))}
                  </ul>
                </section>
              </div>

              <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
                <section className="overflow-hidden rounded-xl border border-line bg-card xl:col-span-2">
                  <div className="flex items-center justify-between gap-2 border-b border-line px-4 py-2.5">
                    <h2 className="text-sm font-semibold text-content">{t("rondines.dashboard.map")}</h2>
                    <select aria-label={t("fields.route")} value={activeRoute?.id ?? ""} onChange={(e) => setRouteId(e.target.value)} className="max-w-xs rounded-lg border border-line bg-app px-2 py-1 text-xs text-content">
                      {routes.map((r) => (
                        <option key={r.id} value={r.id}>
                          {r.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {points.length ? <SiteMap points={points} path pathColor={activeRoute?.color || undefined} className="h-72 w-full" /> : <p className="p-6 text-center text-xs text-content-muted">{t("rondines.dashboard.noGeo")}</p>}
                </section>

                <section className="rounded-xl border border-line bg-card p-4">
                  <h2 className="mb-3 text-sm font-semibold text-content">{t("rondines.dashboard.ranking")}</h2>
                  <ul className="space-y-2.5">
                    {d.byRoute.slice(0, 8).map((r) => (
                      <li key={r.routeId}>
                        <div className="mb-1 flex items-center justify-between gap-2 text-xs">
                          <span className="flex min-w-0 items-center gap-1.5 text-content">
                            <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: r.color || "currentColor" }} />
                            <span className="truncate">{r.name}</span>
                          </span>
                          <span className="shrink-0 tabular-nums text-content-secondary">{r.compliancePct === null ? "—" : `${r.compliancePct}%`}</span>
                        </div>
                        <ProgressBar pct={r.compliancePct ?? 0} tone={pctTone(r.compliancePct)} />
                      </li>
                    ))}
                  </ul>
                  {d.mostSkipped.length ? (
                    <>
                      <h3 className="mb-2 mt-5 text-xs font-semibold uppercase tracking-wide text-content-secondary">{t("rondines.dashboard.mostSkipped")}</h3>
                      <ul className="space-y-1.5 text-xs">
                        {d.mostSkipped.map((c) => (
                          <li key={c.checkpointId} className="flex justify-between gap-2 text-content">
                            <span className="truncate">{c.name}</span>
                            <span className="shrink-0 text-error">{t("rondines.dashboard.skippedTimes", { n: c.skipped })}</span>
                          </li>
                        ))}
                      </ul>
                    </>
                  ) : null}
                </section>
              </div>

              <section className="rounded-xl border border-line bg-card p-4">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-content">{t("rondines.dashboard.recentIncidents")}</h2>
                  {d.guards.length ? (
                    <span className="flex items-center gap-2 text-xs text-content-secondary">
                      {t("rondines.dashboard.guards", { n: d.guards.length })} <AvatarStack names={d.guards.map((g) => g.name)} />
                    </span>
                  ) : null}
                </div>
                {d.incidents.recent.length === 0 ? <p className="text-xs text-content-muted">{t("rondines.incidents.emptyTitle")}</p> : null}
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
                  {d.incidents.recent.map((i) => (
                    <div key={i.id} className="overflow-hidden rounded-lg border border-line">
                      <Photo src={i.photos[0]} alt={i.title} thumb className="aspect-[16/9] w-full" icon="ri-alarm-warning-line" />
                      <div className="space-y-1 p-2">
                        <SeverityBadge severity={i.severity} />
                        <div className="truncate text-xs font-semibold text-content">{i.title}</div>
                        <div className="truncate text-[11px] text-content-muted">
                          {i.baseName} · {relativeTime(i.reportedAt, i18n.language)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          ) : null}
        </LoadBlock>
      </div>
    </div>
  );
}
